import {findIndex, isFunction} from 'lodash';

export function updateComplexObject<
  T extends Record<string, any>,
  S extends Record<string, any>
>(item: T, itemChange: S): T & S {
  const newItem = item && item.clone ? item.clone() : {...item};
  return Object.assign(newItem, itemChange);
}

export function updateItemInArray<T extends Record<string, any>>(
  array: T[],
  itemChange: Record<string, any>,
  itemIdx: number
): T[] {
  return array.map((item, index) => {
    if (index !== itemIdx) {
      // This isn't the item we care about - keep it as-is
      return item;
    }

    // Otherwise, this is the one we want - return an updated value
    return updateComplexObject(item, itemChange);
  });
}

export function removeItemFromArray<T>(
  array: T[],
  itemIdxOrPredicate: number | ((item: T) => boolean)
): T[] {
  let itemIdx: number;
  if (isFunction(itemIdxOrPredicate)) {
    itemIdx = findIndex(array, itemIdxOrPredicate);
  } else {
    itemIdx = itemIdxOrPredicate;
  }
  if (itemIdx >= 0 && itemIdx < array.length) {
    return [...array.slice(0, itemIdx), ...array.slice(itemIdx + 1)];
  }
  return [...array];
}

/**
 * Immutable remove property from object.
 * @param obj Object from which the property is removed.
 * @param key Name of the removed property.
 * @returns New object without the removed property.
 */
export function deleteProperty(
  obj: Record<string, any>,
  key: string
): Record<string, any> {
  const {[key]: _unused, ...newObj} = obj;
  return newObj;
}

export default {
  deleteProperty,
  removeItemFromArray,
  updateItemInArray,
  updateComplexObject,
};
